/**
 * Minimal line diff for the CLAUDE.md editor's 預覽變更.
 *
 * A classic LCS table: the inputs are capped at 128 KB by contract, so the
 * quadratic table is bounded, and a dependency-free implementation keeps the
 * bundle honest for a feature this small.
 */

export interface DiffLine {
  readonly type: 'context' | 'add' | 'remove';
  readonly text: string;
}

const CONTEXT = 2;

export function diffLines(before: string, after: string): DiffLine[] {
  const a = before.split('\n');
  const b = after.split('\n');

  const table: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );
  for (let i = a.length - 1; i >= 0; i -= 1) {
    const row = table[i];
    const nextRow = table[i + 1];
    if (!row || !nextRow) continue;
    for (let j = b.length - 1; j >= 0; j -= 1) {
      row[j] = a[i] === b[j] ? (nextRow[j + 1] ?? 0) + 1 : Math.max(nextRow[j] ?? 0, row[j + 1] ?? 0);
    }
  }

  const full: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      full.push({ type: 'context', text: a[i] ?? '' });
      i += 1;
      j += 1;
    } else if ((table[i + 1]?.[j] ?? 0) >= (table[i]?.[j + 1] ?? 0)) {
      full.push({ type: 'remove', text: a[i] ?? '' });
      i += 1;
    } else {
      full.push({ type: 'add', text: b[j] ?? '' });
      j += 1;
    }
  }
  while (i < a.length) {
    full.push({ type: 'remove', text: a[i] ?? '' });
    i += 1;
  }
  while (j < b.length) {
    full.push({ type: 'add', text: b[j] ?? '' });
    j += 1;
  }

  return collapse(full);
}

/** Keep changed lines plus `CONTEXT` unchanged lines either side. */
function collapse(lines: readonly DiffLine[]): DiffLine[] {
  const keep = new Array<boolean>(lines.length).fill(false);
  lines.forEach((line, index) => {
    if (line.type === 'context') return;
    for (let k = Math.max(0, index - CONTEXT); k <= Math.min(lines.length - 1, index + CONTEXT); k += 1) {
      keep[k] = true;
    }
  });
  const out: DiffLine[] = [];
  lines.forEach((line, index) => {
    if (keep[index]) out.push(line);
  });
  return out;
}

